import { useEffect, useState } from "react";
import {
  getApplicationRetentionPolicy,
  updateApplicationRetentionPolicy,
  type Application,
  type ApplicationRetentionPolicy,
  type RetentionConversationType,
} from "../api";
import { formatDate } from "../presentation";
import { ManagementHeading } from "./ManagementCommon";
import { Button } from "./ui/Button";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import { Icon } from "./ui/Icon";

const types: readonly RetentionConversationType[] = [
  "C2C",
  "GROUP",
  "ROOM",
  "CHANNEL",
  "BROADCAST",
];

const metadata: Record<RetentionConversationType, {
  readonly title: string;
  readonly description: string;
}> = {
  C2C: { title: "单聊", description: "一对一会话的历史消息" },
  GROUP: { title: "群聊", description: "普通群组的历史消息" },
  ROOM: { title: "房间", description: "直播间、公屏等临时消息" },
  CHANNEL: { title: "频道", description: "频道内的公开消息" },
  BROADCAST: { title: "广播", description: "广播会话的消息记录" },
};

export function RetentionPolicyView({ application, applications, onSelect, onNotice, onError }: {
  readonly application: Application | null;
  readonly applications: readonly Application[];
  readonly onSelect: (application: Application) => void;
  readonly onNotice: (message: string) => void;
  readonly onError: (message: string) => void;
}) {
  const [policy, setPolicy] = useState<ApplicationRetentionPolicy | null>(null);
  const [days, setDays] = useState<Record<RetentionConversationType, number>>(() => defaultDays());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDecrease, setConfirmDecrease] = useState(false);

  useEffect(() => {
    if (application === null) {
      setPolicy(null);
      return;
    }
    let active = true;
    setLoading(true);
    void getApplicationRetentionPolicy(application.appId)
      .then((value) => {
        if (!active) return;
        setPolicy(value);
        setDays(policyDays(value));
      })
      .catch((cause: unknown) => { if (active) onError(errorMessage(cause)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [application?.appId, onError]);

  async function save(confirmed = false): Promise<void> {
    if (policy === null) return;
    const decreases = types.some((type) => days[type] < policy.policies[type].historyDays);
    if (decreases && !confirmed) {
      setConfirmDecrease(true);
      return;
    }
    setBusy(true);
    try {
      const effectiveAt = Date.now() + (decreases ? 24 * 60 * 60 * 1_000 + 60_000 : 60_000);
      const updated = await updateApplicationRetentionPolicy(policy, {
        applyScope: "ALL_CONVERSATIONS",
        effectiveAt,
        policies: Object.fromEntries(types.map((type) => [
          type,
          { historyDays: days[type] },
        ])) as ApplicationRetentionPolicy["policies"],
      });
      setPolicy(updated);
      setDays(policyDays(updated));
      setConfirmDecrease(false);
      onNotice(decreases
        ? "保留策略已保存，将在至少 24 小时后发布并应用到存量会话"
        : "保留策略已保存，将在一分钟后发布生效");
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="management-page retention-page">
      <ManagementHeading
        title="数据保留策略"
        description="按会话类型控制消息正文、历史归档和内容投影的保留时间。"
        applications={applications}
        selected={application}
        onSelect={onSelect}
      />
      {application === null ? (
        <div className="empty-page"><strong>请先创建应用</strong><p>数据保留策略严格归属于单个应用。</p></div>
      ) : loading || policy === null ? (
        <div className="management-state"><span className="loader" /><strong>正在加载保留策略</strong></div>
      ) : (
        <div className="retention-workspace">
          <section className="data-card retention-editor">
            <header className="data-card-header retention-header">
              <div><h3>消息存档周期</h3><p>允许范围 1–180 天；所有期限均以服务端消息时间计算。</p></div>
              <span className={`management-tag ${policy.status === "ACTIVE" ? "tag-success" : policy.status === "FAILED" ? "tag-danger" : "tag-info"}`}>
                {policy.status === "ACTIVE" ? "已发布" : policy.status === "FAILED" ? "等待重试" : "发布中"}
              </span>
            </header>
            <div className="retention-grid">
              {types.map((type) => (
                <label className="retention-type-card" key={type}>
                  <span className="retention-type-copy">
                    <strong>{metadata[type].title}</strong>
                    <code>{type}</code>
                    <small>{metadata[type].description}</small>
                  </span>
                  <span className="retention-input">
                    <input
                      aria-label={`${metadata[type].title}保留天数`}
                      min={1}
                      max={180}
                      type="number"
                      value={days[type]}
                      onChange={(event) => {
                        const value = Number(event.currentTarget.value);
                        setDays((current) => ({ ...current, [type]: value }));
                      }}
                    />
                    <span>天</span>
                  </span>
                </label>
              ))}
            </div>
            <footer className="retention-actions">
              <div>
                <small>当前版本 v{policy.version}</small>
                <span>计划生效：{formatDate(policy.effectiveAt)}</span>
              </div>
              <Button
                loading={busy}
                variant="primary"
                disabled={types.some((type) => !Number.isInteger(days[type]) || days[type] < 1 || days[type] > 180)}
                onClick={() => { void save(); }}
              >保存策略</Button>
            </footer>
          </section>

          <aside className="surface-card retention-boundary">
            <span><Icon name="warning" size={18} /></span>
            <div>
              <h3>数据边界</h3>
              <p>策略不会删除会话、群成员、seq、已读游标或未读状态，也不会改变消息 ACK 和实时投递语义。</p>
              <p><strong>媒体文件仍按独立的 90 天策略保留。</strong>缩短消息期限不会同步删除图片、语音、视频或文件对象。</p>
              <p>缩短策略至少延迟 24 小时生效。物理删除后的消息无法通过回滚恢复。</p>
            </div>
          </aside>
        </div>
      )}
      {confirmDecrease ? <ConfirmDialog busy={busy} confirmLabel="确认缩短保留期" consequences={["策略至少延迟 24 小时生效", "到期消息将被永久物理删除", "代码回滚无法恢复已删除消息"]} description="缩短保留时间是不可逆的数据治理操作。" title="确认修改消息保留策略" onClose={() => { setConfirmDecrease(false); }} onConfirm={() => { void save(true); }} /> : null}
    </div>
  );
}

function defaultDays(): Record<RetentionConversationType, number> {
  return { C2C: 180, GROUP: 180, ROOM: 1, CHANNEL: 1, BROADCAST: 1 };
}

function policyDays(policy: ApplicationRetentionPolicy): Record<RetentionConversationType, number> {
  return Object.fromEntries(types.map((type) => [type, policy.policies[type].historyDays])) as
    Record<RetentionConversationType, number>;
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
